import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Copy,
  QrCode,
  Plus,
  Download,
  Eye,
  EyeOff,
  LogOut,
  RefreshCw,
  LinkIcon,
  Users,
  FileText,
  Wallet,
  CalendarClock,
  Coins,
  ArrowUpRight,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import RenewalBanner from "../components/RenewalBanner";
import AdminWithdrawals from "../components/AdminWithdrawals";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { generateAuditPdf } from "../lib/pdf";

const TIER_CLASS = {
  Critical: "tier-critical",
  Exposed: "tier-exposed",
  Performing: "tier-performing",
  Elite: "tier-elite",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const isBusiness = user?.license_type === "business";
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("links");
  const [links, setLinks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [affiliate, setAffiliate] = useState(null);

  const [days, setDays] = useState("30");
  const [tierFilter, setTierFilter] = useState("all");
  const [revealed, setRevealed] = useState({});
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newCustomSlug, setNewCustomSlug] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const calls = [
        api.get("/audit-links/me"),
        api.get(`/reports/summary?days=${days}`),
        api.get("/affiliate/stats"),
      ];
      if (isBusiness) {
        calls.push(api.get(`/reports/departments?days=${days}`));
      } else {
        calls.push(api.get(`/leads?days=${days}${tierFilter !== "all" ? `&tier=${tierFilter}` : ""}`));
      }
      const [linksRes, sumRes, affRes, lastRes] = await Promise.all(calls);
      setLinks(linksRes.data);
      setSummary(sumRes.data);
      setAffiliate(affRes.data);
      if (isBusiness) {
        setDepartments(lastRes.data);
      } else {
        setLeads(lastRes.data);
      }
    } catch (e) {
      toast({ title: "Failed to load", description: formatApiError(e), variant: "destructive" });
    }
  }, [days, tierFilter, toast, isBusiness]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const buildAuditUrl = (slug) => `${window.location.origin}/audit/${slug}`;

  const copy = async (val, what = "Copied") => {
    try {
      await navigator.clipboard.writeText(val);
      toast({ title: what, description: val });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const downloadQr = (slug) => {
    const canvas = document.getElementById(`qr-${slug}`);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `fitest-audit-${slug}.png`;
    a.click();
  };

  const createLink = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      await api.post("/audit-links", {
        label: newLabel.trim(),
        custom_slug: newCustomSlug.trim() || null,
      });
      setNewLabel("");
      setNewCustomSlug("");
      await fetchAll();
      toast({ title: "Audit link created" });
    } catch (e) {
      toast({ title: "Create failed", description: formatApiError(e), variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const reveal = async (leadId) => {
    try {
      const { data } = await api.get(`/leads/${leadId}/email`);
      setRevealed((r) => ({ ...r, [leadId]: data.email }));
    } catch (e) {
      toast({ title: "Cannot reveal", description: formatApiError(e), variant: "destructive" });
    }
  };

  const downloadLeadPdf = async (leadId) => {
    try {
      const { data } = await api.get(`/leads/${leadId}/full`);
      generateAuditPdf({
        name: data.name,
        score: data.score,
        tier: data.tier,
        ownerName: data.owner_name,
        ownerCompany: data.owner_company,
        answers: data.answers,
        questions: data.questions,
      });
    } catch (e) {
      toast({ title: "Download failed", description: formatApiError(e), variant: "destructive" });
    }
  };

  const downloadCsv = async (d) => {
    try {
      const res = await fetch(`${API}/reports/csv?days=${d}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fitest-report-${d}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };

  const withdraw = async () => {
    setWithdrawing(true);
    try {
      const { data } = await api.post("/affiliate/withdraw", {});
      toast({
        title: "Withdrawal request received.",
        description: `£${data.withdrawal.amount.toFixed(2)} logged. Payouts are processed manually on the 1st of every month via Stripe.`,
      });
      await fetchAll();
    } catch (e) {
      toast({ title: "Withdrawal failed", description: formatApiError(e), variant: "destructive" });
    } finally {
      setWithdrawing(false);
    }
  };

  const referralFullUrl = useMemo(() => {
    if (!affiliate?.user_id) return "";
    return `${window.location.origin}/?ref=${affiliate.user_id}`;
  }, [affiliate]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <RenewalBanner />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <span className="eyebrow">Licence Holder Dashboard</span>
          <h1 className="font-display tracking-tighter text-4xl mt-3">
            Welcome back, {user?.name?.split(" ")[0] || "Operator"}.
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {user?.company || "Fitest"} · {user?.license_type || "gym"} licence
            {user?.license_active ? <span className="ml-2 text-tier-performing">· active</span> : <span className="ml-2 text-tier-exposed">· awaiting activation</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchAll} variant="outline" className="rounded-full bg-transparent border-white/15" data-testid="dashboard-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={logout} variant="outline" className="rounded-full bg-transparent border-white/15" data-testid="dashboard-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      {/* Summary Bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="dashboard-summary">
        <SummaryCard label="Total Submissions" value={summary?.total ?? 0} hint={`Last ${days} days`} />
        <SummaryCard label="Avg Score" value={summary?.average_score ?? 0} hint="0–100" />
        <SummaryCard label="Opt-ins" value={summary?.opt_in_count ?? 0} hint={isBusiness ? "Anonymised view" : "Email revealable"} />
        <SummaryCard
          label="Tier Mix"
          value={
            <div className="flex gap-1.5 flex-wrap mt-1">
              {["Critical", "Exposed", "Performing", "Elite"].map((t) => (
                <span key={t} className={`px-2 py-0.5 rounded-full text-[10px] border ${TIER_CLASS[t]}`}>
                  {summary?.tiers?.[t] ?? 0}
                </span>
              ))}
            </div>
          }
          hint=""
        />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="dashboard-tabs">
        <TabsList className="bg-white/5 border border-white/10 rounded-full p-1 h-11 mb-6">
          <TabsTrigger value="links" className="rounded-full px-4 data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-links">
            <LinkIcon className="w-4 h-4 mr-2" /> Audit Links
          </TabsTrigger>
          <TabsTrigger value="leads" className="rounded-full px-4 data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-leads">
            <Users className="w-4 h-4 mr-2" /> {isBusiness ? "Departments" : "Leads"}
          </TabsTrigger>
          <TabsTrigger value="reports" className="rounded-full px-4 data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-reports">
            <FileText className="w-4 h-4 mr-2" /> Reports
          </TabsTrigger>
          <TabsTrigger value="affiliate" className="rounded-full px-4 data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-affiliate">
            <Wallet className="w-4 h-4 mr-2" /> Affiliate
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="rounded-full px-4 data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-admin">
              <Wallet className="w-4 h-4 mr-2" /> Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* AUDIT LINKS */}
        <TabsContent value="links">
          <section className="glass rounded-2xl p-6" data-testid="audit-generator">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-xl">Audit Links</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Generate a unique audit link to drop into comms or print as a QR. Custom slugs supported.
                </p>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="btn-glow rounded-full" data-testid="create-audit-link-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Link
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0D1B2A] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle>Create new audit link</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-slate-300">Label</Label>
                      <Input
                        placeholder="e.g. Reception QR — Q1 2026"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        className="bg-white/5 border-white/10 mt-1"
                        data-testid="new-link-label"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-slate-300">
                        Custom slug <span className="text-slate-500 normal-case tracking-normal">(optional)</span>
                      </Label>
                      <Input
                        placeholder="e.g. city-gym-london"
                        value={newCustomSlug}
                        onChange={(e) => setNewCustomSlug(e.target.value)}
                        className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
                        data-testid="new-link-slug"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Will become <span className="text-brand">/audit/{newCustomSlug.trim() || "auto-generated"}</span>. Lowercase letters, numbers and dashes only.
                      </p>
                    </div>
                    <Button onClick={createLink} disabled={creating} className="btn-glow w-full rounded-full" data-testid="confirm-create-link">
                      {creating ? "Creating…" : "Create link"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {links.length === 0 ? (
              <p className="text-slate-500 text-sm py-6 text-center">No audit links yet. Create one to start collecting submissions.</p>
            ) : (
              <div className="space-y-3">
                {links.map((l) => (
                  <div
                    key={l.slug}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5"
                    data-testid={`audit-link-${l.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm text-white">{l.label}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">{buildAuditUrl(l.slug)}</div>
                      <div className="text-[10px] text-slate-500 mt-1">{l.submission_count} submissions</div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button onClick={() => copy(buildAuditUrl(l.slug))} variant="outline" className="bg-transparent border-white/15 rounded-full" data-testid={`copy-link-${l.slug}`}>
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="bg-transparent border-white/15 rounded-full" data-testid={`qr-link-${l.slug}`}>
                            <QrCode className="w-4 h-4 mr-1" /> QR
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#0D1B2A] border-white/10 text-white max-w-sm">
                          <DialogHeader>
                            <DialogTitle>QR Code — {l.label}</DialogTitle>
                          </DialogHeader>
                          <div className="bg-white p-4 rounded-xl flex justify-center my-4">
                            <QRCodeCanvas id={`qr-${l.slug}`} value={buildAuditUrl(l.slug)} size={220} />
                          </div>
                          <Button onClick={() => downloadQr(l.slug)} className="btn-glow w-full rounded-full" data-testid={`download-qr-${l.slug}`}>
                            <Download className="w-4 h-4 mr-1" /> Download QR PNG
                          </Button>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </TabsContent>

        {/* LEADS / DEPARTMENTS */}
        <TabsContent value="leads">
          {isBusiness ? (
            <section className="glass rounded-2xl p-6" data-testid="departments-section">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h2 className="font-display text-xl">Departmental Health Benchmarking</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Anonymised aggregates only — Business licence holders never see individual names or emails.
                  </p>
                </div>
                <Select value={days} onValueChange={(v) => setDays(v)}>
                  <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid="dept-days-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D1B2A] border-white/10 text-white">
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="365">Last 365 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto rounded-xl border border-white/5">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">Department</TableHead>
                      <TableHead className="text-slate-400">Submissions</TableHead>
                      <TableHead className="text-slate-400">Avg Score</TableHead>
                      <TableHead className="text-slate-400">Critical</TableHead>
                      <TableHead className="text-slate-400">Exposed</TableHead>
                      <TableHead className="text-slate-400">Performing</TableHead>
                      <TableHead className="text-slate-400">Elite</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.length === 0 ? (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">No submissions yet.</TableCell>
                      </TableRow>
                    ) : (
                      departments.map((d) => (
                        <TableRow key={d.department} className="border-white/5 hover:bg-white/[0.03]" data-testid={`dept-row-${d.department}`}>
                          <TableCell className="font-medium text-white">{d.department}</TableCell>
                          <TableCell className="font-mono">{d.total}</TableCell>
                          <TableCell className="font-mono">{d.average_score}</TableCell>
                          <TableCell className="text-tier-critical font-mono">{d.tiers.Critical}</TableCell>
                          <TableCell className="text-tier-exposed font-mono">{d.tiers.Exposed}</TableCell>
                          <TableCell className="text-tier-performing font-mono">{d.tiers.Performing}</TableCell>
                          <TableCell className="text-tier-elite font-mono">{d.tiers.Elite}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>
          ) : (
            <section className="glass rounded-2xl p-6" data-testid="leads-section">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-xl">Leads</h2>
                  <p className="text-xs text-slate-500 mt-1">Email is hidden unless the respondent opted in for outreach.</p>
                </div>
                <div className="flex gap-2">
                  <Select value={days} onValueChange={(v) => setDays(v)}>
                    <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid="leads-days-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0D1B2A] border-white/10 text-white">
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="365">Last 365 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tierFilter} onValueChange={(v) => setTierFilter(v)}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10" data-testid="leads-tier-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0D1B2A] border-white/10 text-white">
                      <SelectItem value="all">All tiers</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="Exposed">Exposed</SelectItem>
                      <SelectItem value="Performing">Performing</SelectItem>
                      <SelectItem value="Elite">Elite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-white/5">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">#</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Score</TableHead>
                      <TableHead className="text-slate-400">Tier</TableHead>
                      <TableHead className="text-slate-400">Opt-in</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Submitted</TableHead>
                      <TableHead className="text-slate-400">Report</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.length === 0 ? (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">No leads yet.</TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id} className="border-white/5 hover:bg-white/[0.03]" data-testid={`lead-row-${lead.id}`}>
                          <TableCell className="font-mono text-slate-500 text-xs">#{lead.sequence_number ?? "—"}</TableCell>
                          <TableCell className="font-medium text-white">{lead.name}</TableCell>
                          <TableCell className="font-mono">{lead.score}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-0.5 rounded-full text-xs border ${TIER_CLASS[lead.tier]}`}>{lead.tier}</span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {lead.opt_in ? <span className="text-tier-performing">Opted in</span> : <span className="text-slate-500">Not opted</span>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {lead.email ? (
                              <span className="text-slate-200">{lead.email}</span>
                            ) : revealed[lead.id] ? (
                              <span className="text-slate-200">{revealed[lead.id]}</span>
                            ) : lead.opt_in ? (
                              <Button size="sm" onClick={() => reveal(lead.id)} variant="outline" className="bg-transparent border-white/15 rounded-full h-7 text-xs" data-testid={`reveal-email-${lead.id}`}>
                                <Eye className="w-3.5 h-3.5 mr-1" /> Reveal
                              </Button>
                            ) : (
                              <span className="text-slate-600 text-xs inline-flex items-center"><EyeOff className="w-3.5 h-3.5 mr-1" /> Hidden</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(lead.submitted_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => downloadLeadPdf(lead.id)}
                              variant="outline"
                              className="bg-transparent border-white/15 rounded-full h-7 text-xs"
                              data-testid={`download-pdf-${lead.id}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>
          )}
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports">
          <section className="glass rounded-2xl p-6" data-testid="reports-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-xl">One-click reports</h2>
                <p className="text-xs text-slate-500 mt-1">Download a CSV export for the last 7, 14, or 30 days.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {[7, 14, 30].map((d) => (
                <Button key={d} onClick={() => downloadCsv(d)} variant="outline" className="bg-transparent border-white/15 rounded-full" data-testid={`download-csv-${d}`}>
                  <Download className="w-4 h-4 mr-2" /> {d}-day CSV
                </Button>
              ))}
            </div>
          </section>
        </TabsContent>

        {/* AFFILIATE */}
        <TabsContent value="affiliate">
          <section className="space-y-6" data-testid="affiliate-section">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-display text-xl">Affiliate Revenue Portal</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Earn 20% recurring on every paying licence you refer. {affiliate?.payout_schedule || "Payouts are processed on the 1st of every month."}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Your unique referral link</Label>
                <div className="mt-2 flex gap-2">
                  <Input readOnly value={referralFullUrl} className="bg-white/5 border-white/10 font-mono text-xs h-11" data-testid="affiliate-referral-url" />
                  <Button onClick={() => copy(referralFullUrl, "Link copied")} className="btn-glow rounded-full h-11 px-5" data-testid="affiliate-copy-link">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard label="Total Clicks" value={affiliate?.clicks ?? 0} hint="All time" />
              <SummaryCard label="Licence Signups" value={affiliate?.active_signups ?? 0} hint={`${affiliate?.signups ?? 0} total registrations`} />
              <SummaryCard label="Annual Commission" value={`£${(affiliate?.annual_commission ?? affiliate?.monthly_commission ?? 0).toFixed(2)}`} hint="20% per active referral / year" />
              <SummaryCard label="Lifetime Earnings" value={`£${(affiliate?.lifetime_earnings ?? 0).toFixed(2)}`} hint={`Withdrawn: £${(affiliate?.withdrawn_to_date ?? 0).toFixed(2)}`} />
            </div>

            <div className="glass rounded-2xl p-6" data-testid="affiliate-withdrawal">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-brand">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg">Available balance</h3>
                  <p className="text-xs text-slate-500">Payouts processed on the 1st of every month via Stripe Express.</p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-6">
                <div>
                  <div className="font-display text-5xl text-white" data-testid="affiliate-balance">
                    £{(affiliate?.available_balance ?? 0).toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5" /> {affiliate?.payout_schedule || "Payouts are processed on the 1st of every month."}
                  </div>
                </div>
                <Button
                  onClick={withdraw}
                  disabled={withdrawing || (affiliate?.available_balance ?? 0) <= 0}
                  className="btn-glow rounded-full h-12 px-7"
                  data-testid="affiliate-withdraw-btn"
                >
                  {withdrawing ? "Requesting…" : "Withdraw to Stripe"} <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                Note: payouts are processed manually on the 1st of every month via Stripe while we finalise our automated Stripe Express integration.
                Click "Withdraw to Stripe" to log your request — we'll settle it on the next scheduled payout date.
              </p>
            </div>
          </section>
        </TabsContent>

        {/* ADMIN — Withdrawal Requests Ledger */}
        {isAdmin && (
          <TabsContent value="admin">
            <AdminWithdrawals />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SummaryCard({ label, value, hint }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass rounded-2xl p-5"
      data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display text-3xl mt-2 text-white">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </motion.div>
  );
}
